import { Link } from 'react-router-dom';
import { ArrowRight, Clock, Award, Users, Calendar, Star, Maximize, Volume2, VolumeX } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Helmet } from 'react-helmet-async';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '@/context/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Home() {
  const [isMuted, setIsMuted] = useState(true);
  const videoRef = useRef(null);
  const [siteContent, setSiteContent] = useState(null);
  const { lang, t } = useLanguage();

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/site-content`).then(res => setSiteContent(res.data)).catch(() => {});
  }, []);

  // Homepage hero subtitle translations 
  const heroSubtitleEN = "Officina Martorana Car Service is part of the BOSCH CAR SERVICE network. The workshop is located in Sciacca (AG) Sicily. We take care of all car brands and models with highly qualified professionals and technologically advanced equipment. We operate with professionalism and transparency, with particular attention to optimizing interventions to minimize costs. Trust the experience of four generations for the care of your car.";
  const heroSubtitleIT = siteContent?.hero_subtitle || "Officina Martorana Car Service è parte del network BOSCH CAR SERVICE. L'officina si trova a Sciacca (AG) Sicilia, ci occupiamo della sicurezza e del benessere delle auto di tutte le marche e modelli con professionisti altamente qualificati e strumentazione tecnologicamente avanzata.";
  
  const heroTitle = lang === 'en' ? (siteContent?.hero_title_en || t('home', 'heroTitleFallback')) : (siteContent?.hero_title || t('home', 'heroTitleFallback'));
  const heroSubtitle = lang === 'en' ? heroSubtitleEN : heroSubtitleIT;

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(!isMuted);
    }
  };

  const toggleFullscreen = () => {
    if (videoRef.current) {
      if (videoRef.current.requestFullscreen) videoRef.current.requestFullscreen();
      else if (videoRef.current.webkitRequestFullscreen) videoRef.current.webkitRequestFullscreen();
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Officina Martorana | Bosch Car Service - Sciacca</title>
        <meta name="description" content="Officina Martorana - Bosch Car Service a Sciacca (AG). Oltre 50 anni di eccellenza nell'automotive. Revisioni, tagliandi, riparazioni, soccorso stradale 24h." />
        <meta name="keywords" content="officina, meccanico, Sciacca, Bosch Car Service, revisione auto, tagliando, riparazioni, soccorso stradale" />
        <meta property="og:title" content="Officina Martorana | Bosch Car Service" />
        <meta property="og:description" content="Oltre 50 anni di eccellenza nel settore automobilistico. Partner ufficiale Bosch Car Service." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://officinamartorana.com" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "AutoRepair",
          "name": "Officina Martorana Bosch Car Service",
          "address": { "@type": "PostalAddress", "streetAddress": "Via Pompei 7", "addressLocality": "Sciacca", "postalCode": "92019", "addressRegion": "AG", "addressCountry": "IT" },
          "telephone": "+390925259520",
          "openingHoursSpecification": { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday"], "opens": "08:30", "closes": "19:00" }
        })}</script>
      </Helmet>
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 via-white to-gray-50 relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-blue-100/40 via-transparent to-transparent" />
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-block">
                <div className="bg-gradient-to-r from-[#0066b2] to-[#005494] text-white px-6 py-2 rounded-full text-sm font-medium shadow-lg">
                  <a href="https://www.boschcarservice.com" target="_blank" rel="noopener noreferrer" className="flex items-center space-x-2 hover:opacity-90 transition-opacity">
                    <img src="/bosch-car-service-logo.png" alt="Bosch Car Service" className="w-5 h-5 object-contain" />
                    <span>{t('home', 'badge')}</span>
                  </a>
                </div>
              </div>
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight" style={{ fontFamily: 'Space Grotesk' }} data-testid="hero-title">
                {heroTitle}
              </h1>
              <p className="text-lg text-gray-600 leading-relaxed" data-testid="hero-subtitle">
                {heroSubtitle}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/prenota">
                  <Button size="lg" className="bg-[#0066b2] hover:bg-[#005494] text-white px-8 py-6 text-base font-medium shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all" data-testid="hero-book-button">
                    {t('home', 'bookNow')} <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/servizi">
                  <Button size="lg" variant="outline" className="px-8 py-6 text-base font-medium border-2 border-gray-300 hover:border-[#0066b2] hover:text-[#0066b2] transition-all" data-testid="hero-services-button">
                    {t('home', 'discoverServices')}
                  </Button>
                </Link>
              </div>
              <div className="grid grid-cols-3 gap-6 pt-4">
                <div className="text-center" data-testid="stat-years">
                  <div className="text-3xl font-bold text-[#0066b2]" style={{ fontFamily: 'Space Grotesk' }}>{siteContent?.stat_years || '50+'}</div>
                  <div className="text-sm text-gray-600 mt-1">{t('home', 'statYears')}</div>
                </div>
                <div className="text-center" data-testid="stat-customers">
                  <div className="text-3xl font-bold text-[#0066b2]" style={{ fontFamily: 'Space Grotesk' }}>{siteContent?.stat_customers || '90000+'}</div>
                  <div className="text-sm text-gray-600 mt-1">{t('home', 'statCustomers')}</div>
                </div>
                <div className="text-center" data-testid="stat-services">
                  <div className="text-3xl font-bold text-[#0066b2]" style={{ fontFamily: 'Space Grotesk' }}>{siteContent?.stat_services || '10+'}</div>
                  <div className="text-sm text-gray-600 mt-1">{t('home', 'statServices')}</div>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="relative z-10 rounded-3xl overflow-hidden shadow-2xl group">
                <video ref={videoRef} src="https://customer-assets.emergentagent.com/job_officina-smart/artifacts/ac78k1rp_55bbbc6ad9bc430fb9c3108fe9cbb9be%202.MP4" autoPlay loop muted playsInline className="w-full max-w-md mx-auto h-auto aspect-[9/16] object-cover" data-testid="hero-video" />
                <div className="absolute bottom-4 right-4 flex space-x-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <button onClick={toggleMute} className="w-12 h-12 bg-black/60 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-black/80 transition-colors" data-testid="video-mute-toggle">
                    {isMuted ? <VolumeX className="w-6 h-6 text-white" /> : <Volume2 className="w-6 h-6 text-white" />}
                  </button>
                  <button onClick={toggleFullscreen} className="w-12 h-12 bg-black/60 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-black/80 transition-colors" data-testid="video-fullscreen-toggle">
                    <Maximize className="w-6 h-6 text-white" />
                  </button>
                </div>
              </div>
              <div className="absolute -top-6 -right-6 w-72 h-72 bg-[#0066b2]/10 rounded-full blur-3xl" />
              <div className="absolute -bottom-6 -left-6 w-72 h-72 bg-blue-300/20 rounded-full blur-3xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white" data-testid="team-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Space Grotesk' }}>{t('home', 'teamTitle')}</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">{t('home', 'teamSubtitle')}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { img: siteContent?.team_mechanic_url || "https://customer-assets.emergentagent.com/job_officina-smart/artifacts/1q8zvdsh_114.jpg", role: t('home', 'teamMechanics') },
              { img: siteContent?.team_revision_url || "https://customer-assets.emergentagent.com/job_officina-smart/artifacts/yixrrvo9_115.jpg", role: t('home', 'teamRevisions') },
              { img: siteContent?.team_office_url || "https://customer-assets.emergentagent.com/job_officina-smart/artifacts/effwi0qb_116.jpg", role: t('home', 'teamOffice') },
            ].map((member, idx) => (
              <div key={idx} className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow duration-300" data-testid={`team-member-${idx}`}>
                <div className="h-96 flex items-center justify-center bg-gradient-to-br from-gray-50 to-white p-4">
                  <img src={member.img} alt={member.role} className="w-full h-full object-contain" />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Space Grotesk' }}>{t('home', 'team')}</h3>
                  <p className="text-[#0066b2] font-medium">{member.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-white" data-testid="values-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Space Grotesk' }}>{t('home', 'valuesTitle')}</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">{t('home', 'valuesSubtitle')}</p>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { icon: Award, title: t('home', 'excellence'), desc: t('home', 'excellenceDesc') },
              { icon: Clock, title: t('home', 'punctuality'), desc: t('home', 'punctualityDesc') },
              { icon: Users, title: t('home', 'professionalism'), desc: t('home', 'professionalismDesc') },
              { icon: Star, title: t('home', 'transparency'), desc: t('home', 'transparencyDesc') },
            ].map((value, idx) => (
              <div key={idx} className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-2xl border border-gray-200 hover:border-[#0066b2] hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2" data-testid={`value-card-${idx}`}>
                <div className="w-16 h-16 bg-gradient-to-br from-[#0066b2] to-[#005494] rounded-2xl flex items-center justify-center mb-6 transform hover:rotate-6 transition-transform shadow-lg">
                  <value.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3" style={{ fontFamily: 'Space Grotesk' }}>{value.title}</h3>
                <p className="text-gray-600">{value.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Authorized Partners Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white" data-testid="partners-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Space Grotesk' }}>{t('home', 'authorized')}</h2>
          </div>
          <div className="flex flex-col md:flex-row items-center justify-center gap-12 md:gap-20">
            <a href="https://www.renault.it" target="_blank" rel="noopener noreferrer" className="flex flex-col items-center" data-testid="renault-logo">
              <img src="https://customer-assets.emergentagent.com/job_officina-smart/artifacts/d4cvh1ti_Renault-2025-Logo-Vector.svg-.png" alt="Renault" className="h-24 w-auto object-contain grayscale hover:grayscale-0 transition-all duration-300 transform hover:scale-110" />
            </a>
            <a href="https://www.dacia.it" target="_blank" rel="noopener noreferrer" className="flex flex-col items-center" data-testid="dacia-logo">
              <img src="https://customer-assets.emergentagent.com/job_officina-smart/artifacts/esxsmo01_Dacia.png" alt="Dacia" className="h-24 w-auto object-contain grayscale hover:grayscale-0 transition-all duration-300 transform hover:scale-110" />
            </a>
            <a href="https://www.boschcarservice.com" target="_blank" rel="noopener noreferrer" className="flex flex-col items-center" data-testid="bosch-logo">
              <img src="/bosch-car-service-logo.png" alt="Bosch Car Service" className="h-24 w-auto object-contain grayscale hover:grayscale-0 transition-all duration-300 transform hover:scale-110" />
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-[#0066b2] to-[#005494] text-white relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS1vcGFjaXR5PSIwLjA1IiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-30" />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <Calendar className="w-16 h-16 mx-auto mb-6" />
          <h2 className="text-4xl font-bold mb-6" style={{ fontFamily: 'Space Grotesk' }}>{t('home', 'ctaTitle')}</h2>
          <p className="text-xl mb-8 text-white/90">{t('home', 'ctaSubtitle')}</p>
          <Link to="/prenota">
            <Button size="lg" className="bg-white text-[#0066b2] hover:bg-gray-100 px-8 py-6 text-base font-medium shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all" data-testid="cta-book-button">
              {t('home', 'ctaButton')} <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
